import {
  Box,
  Grid2,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AdminPageCardTitle from "components/AdminPageCardTitle/AdminPageCardTitle";
import theme, { responsiveFontSize } from "themes/Theme";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import EyeHiddenIcon from "assets/svg/EyeHiddenIcon";
import EyeVisibleIcon from "assets/svg/EyeVisibleIcon";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import { CreateUserRequest } from "reduxStore/dataTypes";
import { adminCreateUser } from "services/apiService";
import { AxiosError, AxiosResponse } from "axios";
import { toast } from "react-toastify";

const AddUserPage = () => {
  const navigate = useNavigate();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });

    // Clear the error when user starts typing
    setErrors({ ...errors, [name]: "" });
  };

  const handleTogglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const validateForm = () => {
    let newErrors = { ...errors };
    let isValid = true;
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formValues.firstName.trim()) {
      newErrors.firstName = "First name is required";
      isValid = false;
    }

    if (!formValues.lastName.trim()) {
      newErrors.lastName = "Last name is required";
      isValid = false;
    }

    if (!formValues.email.trim()) {
      newErrors.email = "Email address is required";
      isValid = false;
    }

    if (!formValues.password.trim()) {
      newErrors.password = "Password is required";
      isValid = false;
    }

    if (!emailRegex.test(formValues.email.trim())) {
      newErrors.email = "Email address is invalid";
      isValid = false;
    }

    if (!passwordRegex.test(formValues.password.trim())) {
      newErrors.password =
        "Password is must contain at least 1 uppercase letter, 1 number, and 1 special character";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleCreateUser = async () => {
    try {
      if (validateForm()) {
        const body: CreateUserRequest = {
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          email: formValues.email,
          password: formValues.password,
        };
        const response: AxiosResponse<any> = await adminCreateUser(body);
        if (response && response.status === 200) {
          toast.success(response?.data?.message);
          navigate("/admin/users");
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error?.response?.data?.message);
      }
    }
  };

  return (
    <Box sx={styles.container}>
      <AdminPageCardTitle
        title={"Create New Admin User"}
        icon={<ArrowBackIcon sx={{ color: theme.palette.primary.main }} />}
        onIconClick={() => {
          navigate(-1);
        }}
      />

      <Grid2 container spacing={2} mt={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Typography sx={styles.inputFieldLabel}>First Name</Typography>
          <TextField
            name="firstName"
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Enter first name"
            value={formValues.firstName}
            onChange={handleInputChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Typography sx={styles.inputFieldLabel}>Last Name</Typography>
          <TextField
            name="lastName"
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Enter last name"
            value={formValues.lastName}
            onChange={handleInputChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Typography sx={styles.inputFieldLabel}>Email Address</Typography>
          <TextField
            name="email"
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Enter email address"
            value={formValues.email}
            onChange={handleInputChange}
            error={!!errors.email}
            helperText={errors.email}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Typography sx={styles.inputFieldLabel}>Password</Typography>
          <TextField
            name="password"
            variant="outlined"
            type={isPasswordVisible ? "text" : "password"}
            size="small"
            fullWidth
            placeholder="Enter password"
            value={formValues.password}
            slotProps={{
              input: {
                sx: {
                  fontSize: "0.875rem",
                  color: theme.palette.text.primary,
                  fontWeight: 400,
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      aria-label="toggle password visibility"
                    >
                      {isPasswordVisible ? (
                        <EyeHiddenIcon />
                      ) : (
                        <EyeVisibleIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            onChange={handleInputChange}
            error={!!errors.password}
            helperText={errors.password}
          />
        </Grid2>
      </Grid2>
      <Stack direction={"row"} justifyContent={"flex-end"} mt={2}>
        <PrimaryButton
          title="Save"
          onClick={() => {
            handleCreateUser();
          }}
        />
      </Stack>
    </Box>
  );
};
export default AddUserPage;
const styles = {
  container: {
    flexGrow: 1,
    borderRadius: 3,
    boxShadow: `rgba(8, 60, 61, 0.27) 0px 1px 5px 0px, rgba(8, 60, 61, 0.06) 0px 1px 5px 0px`,
    backgroundColor: "rgb(255, 255, 255)",
    p: 3,
    ml: { xs: 0, md: -5 },
  },
  inputLabelStyle: {
    fontSize: responsiveFontSize(14),
    fontWeight: 400,
    color: theme.palette.text.primary,
    width: "15em",
  },
  inputValueStyle: {
    fontSize: responsiveFontSize(14),
    fontWeight: 400,
    color: theme.palette.primary.main,
  },
  inputFieldLabel: {
    fontSize: responsiveFontSize(14),
    color: theme.palette.primary.main,
    fontWeight: 400,
    marginBottom: "0.3em",
  },
};
